import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {MediaObjectApiModel} from '../../../../api/models/media-object.api-model';
import {QueryParamsApiModel} from '../../../../api/models/query-params-api.model';
import {ResultListApiModel} from '../../../../api/models/result-list.api.model';
import {UserApiModel} from '../../../../api/models/user.api-model';
import {MediaObjectRestService} from '../../../../api/services/media-object.rest.service';
import {UserRestService} from '../../../../api/services/user.rest.service';
import {FilterParameterApiModel} from '../../../../api/models/filter-parameter.api.model';
import {MediaObjectViewModel} from '../../../../core/models/media-object.view-model';
import {buildUserReferenceFilters} from '../user-reference-filters.util';

@Injectable({providedIn: 'root'})
export class SocialListHydrationService {
  constructor(
    private readonly userRS: UserRestService,
    private readonly mediaRS: MediaObjectRestService,
  ) {
  }

  public getUsersByReferences$(references: any[]): Observable<Map<string, UserApiModel>> {
    const normalizedReferences = references
      .map((reference) => this.extractResourceIri(reference))
      .filter((reference): reference is string => !!reference);
    const params = new QueryParamsApiModel(buildUserReferenceFilters(normalizedReferences));
    if (!params.filters.length) {
      return of(new Map<string, UserApiModel>());
    }

    return this.userRS.getCollection(params).pipe(
      map((resultListAM: ResultListApiModel<UserApiModel>) => {
        return (resultListAM?.records || []).reduce((usersMap, userAM) => {
          usersMap.set(userAM['@id'], userAM);
          return usersMap;
        }, new Map<string, UserApiModel>());
      })
    );
  }

  public getMediaMap$(references: any[]): Observable<Map<string, MediaObjectViewModel>> {
    const params = new QueryParamsApiModel();
    const uniqueIds = Array.from(new Set(
      references
        .map((reference) => this.extractResourceIri(reference))
        .filter((reference): reference is string => !!reference)
    ));

    if (!uniqueIds.length) {
      return of(new Map<string, MediaObjectViewModel>());
    }

    uniqueIds.forEach((mediaId) => params.filters.push(new FilterParameterApiModel('id', mediaId)));

    return this.mediaRS.getMediaObjectCollection(params).pipe(
      map((resultListAM: ResultListApiModel<MediaObjectApiModel>) => {
        return (resultListAM?.records || []).reduce((mediaMap, mediaObjectAM) => {
          mediaMap.set(mediaObjectAM['@id'], new MediaObjectViewModel(mediaObjectAM));
          return mediaMap;
        }, new Map<string, MediaObjectViewModel>());
      })
    );
  }

  public extractResourceIri(reference: any): string | null {
    if (!reference) {
      return null;
    }

    if (typeof reference === 'string') {
      return reference;
    }

    if (typeof reference === 'object' && typeof reference['@id'] === 'string') {
      return reference['@id'];
    }

    return null;
  }
}
